import { useRef, useState } from "react";
import { useQueryClient } from "react-query";

import {
  DefaultButton,
  Heading,
  IconButton,
  IModalProps,
  Modal,
  Selection,
  SelectionMode,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { DeploymentRing } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useDeploymentRings } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { useFeedbackContext } from "../../../libs/stores/index";

import {
  DeploymentRingFilter,
  DeploymentRingFilterValues
} from "../sections/device/field-device-deployment-rings/DeploymentRingFilter";
import { DeploymentRingTable } from "../sections/device/field-device-deployment-rings/DeploymentRingTable";

interface AddDeploymentRingModalProps extends IModalProps {
  show: boolean;
  title: string;
  existingDeploymentRingIds: string[];
  onDismiss: () => void;
  addDeploymentRing: (ring: DeploymentRing) => Promise<any>;
  refreshCacheKey: string;
}

export const AddDeploymentRingModal = ({
  show,
  title,
  existingDeploymentRingIds,
  onDismiss,
  addDeploymentRing,
  refreshCacheKey,
  ...props
}: AddDeploymentRingModalProps) => {
  const theme = useTheme();
  const [selectedDeploymentRings, setSelectedDeploymentRings] = useState<
    DeploymentRing[]
  >([]);

  const queryClient = useQueryClient();
  const { success } = useFeedbackContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selection = useRef(
    new Selection({
      onSelectionChanged() {
        setSelectedDeploymentRings(
          selection.current.getSelection() as DeploymentRing[]
        );
      }
    })
  );

  const query = useDeploymentRings();

  const close = () => {
    setSelectedDeploymentRings([]);
    selection.current.setItems([], true);
    onDismiss();
  };

  const save = async () => {
    setIsSubmitting(true);

    // Sends requests in parallel
    await Promise.all(selectedDeploymentRings.map(addDeploymentRing));

    // Then refetch tenant/device deployment ring cache
    await queryClient.refetchQueries([refreshCacheKey]);

    // Validation message for bulk rings
    success(
      `The following deployment rings were added: ${Array.from(
        selectedDeploymentRings.values()
      )
        .map(arr => arr.displayName)
        .join(", ")}`
    );

    setIsSubmitting(false);
    close();
  };

  const filterAndSort = (
    deploymentRings: DeploymentRing[],
    filter: DeploymentRingFilterValues
  ) =>
    deploymentRings
      .filter(
        d =>
          !existingDeploymentRingIds.includes(d.id) &&
          (filter.name === "" ||
            d.displayName.toLowerCase().includes(filter.name.toLowerCase()))
      )
      .sort((a, b) => {
        if (a.isPublic === b.isPublic) return 0;
        if (a.isPublic) return 1;
        return -1;
      });

  return (
    <Modal isOpen={show} styles={{ main: { minWidth: "90%" } }} {...props}>
      <Stack
        styles={{
          root: { padding: theme.spacing.l1, overflow: "auto", maxHeight: 600 }
        }}
        tokens={{ childrenGap: theme.spacing.s1 }}
      >
        <Stack horizontal horizontalAlign="space-between">
          <Heading variant="modal-heading">{title}</Heading>
          <IconButton onClick={close} iconProps={{ iconName: "Cancel" }} />
        </Stack>

        <QueryStateIndicator<DeploymentRing[]> allowNullOrUndefined {...query}>
          {deploymentRings => (
            <DeploymentRingFilter>
              {({ values }) => (
                <DeploymentRingTable
                  items={filterAndSort(deploymentRings, values)}
                  stickyHeader
                  selectionPreservedOnEmptyClick
                  selectionMode={SelectionMode.multiple}
                  selection={selection.current}
                  setKey="deploymentRing-Table"
                />
              )}
            </DeploymentRingFilter>
          )}
        </QueryStateIndicator>

        <Stack
          horizontal
          horizontalAlign="end"
          tokens={{ childrenGap: theme.spacing.s1 }}
        >
          <DefaultButton
            disabled={isSubmitting || selectedDeploymentRings.length === 0}
            onClick={save}
          >
            <span>
              Add
              {selectedDeploymentRings.length > 0
                ? ` (${selectedDeploymentRings.length})`
                : ""}
            </span>
          </DefaultButton>
          <DefaultButton onClick={close}>Cancel</DefaultButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
